/**
 * SessionContext — client-side session management for batched proposal submissions.
 *
 * Graph mutations are ONLY allowed inside an active edit session.
 * Call `startSession()` to create a backend session, then use `propose()` or
 * `proposeBatch()` to queue local staged edits.
 * `saveSession()` submits the queued batch through the shared proposal-batch API,
 * clears local preview queue, and exits session mode.
 * `cancelSession()` discards the queue and deactivates session mode.
 * It also removes the open backend session.
 * Calling `propose()` outside an active session is a no-op.
 */
import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import { proposalApi } from '../services/api';
import type { BatchProposalItem, BatchProposalNode, ProposalSubmitEntry } from '../types';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Generates a v4-ish UUID using the Web Crypto API with a hex fallback. */
function generateSessionID(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // Fallback: random hex string
  const buf = new Uint8Array(16);
  crypto.getRandomValues(buf);
  const hex = Array.from(buf)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

// ---------------------------------------------------------------------------
// Context shape
// ---------------------------------------------------------------------------

interface SessionContextValue {
  /** True while a session is active (entries accumulate rather than submit immediately). */
  sessionActive: boolean;
  /** The ID of the currently active session (empty when no session is active). */
  currentSessionId: string;
  /** Reactive snapshot of normalized batch proposals queued locally for the active session. */
  batchQueue: readonly BatchProposalItem[];
  /** Legacy preview projection derived from batchQueue for existing UI consumers. */
  queue: readonly ProposalSubmitEntry[];
  /** Number of entries queued in the current session (derived from queue.length). */
  queueLength: number;
  /**
   * Propose a single graph mutation.
   * Only works when a session is active (no-op otherwise).
  * Enqueues the entry; callers apply staged-preview updates themselves after this call.
   */
  propose: (entry: ProposalSubmitEntry, onCommitted?: () => void) => Promise<void>;
  proposeBatch: (entries: BatchProposalItem[], onCommitted?: () => void) => Promise<void>;
  /** Start a new backend session. No-op if a session is already active. */
  startSession: (parentSessionId?: string | null) => void;
  /** Resume an existing open backend session. No-op if a session is already active. */
  resumeSession: (sessionId: string) => void;
  /**
   * Submit all queued entries as a batch, clear the queue, and deactivate session mode.
   * Calls `onCommitted` after a successful save.  Throws if the batch request fails.
   */
  saveSession: (onCommitted?: () => void) => Promise<void>;
  /**
   * Discard the current queue and deactivate session mode.  The graph is unchanged.
   */
  cancelSession: () => Promise<void>;

}

const SessionContext = createContext<SessionContextValue | null>(null);

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export function SessionProvider({ children }: { children: React.ReactNode }) {
  const [sessionActive, setSessionActive] = useState(false);
  const [currentSessionId, setCurrentSessionId] = useState('');
  // queue state drives local preview re-renders.
  const [batchQueue, setBatchQueue] = useState<BatchProposalItem[]>([]);
  const [queue, setQueue] = useState<ProposalSubmitEntry[]>([]);
  const sessionIDRef = useRef<string>('');
  const batchQueueRef = useRef<BatchProposalItem[]>([]);
  const queueRef = useRef<ProposalSubmitEntry[]>([]);
  /** Incremented on each cancel to ignore stale startSession responses. */
  const cancelGenRef = useRef(0);

  const batchNodeToLegacyProperties = useCallback((node?: BatchProposalNode): Record<string, unknown> | undefined => {
    if (!node) return undefined;
    const properties = { ...(node.properties ?? {}) };
    if (Array.isArray(node.aliases) && node.aliases.length > 0) {
      properties.aliases = node.aliases;
    }
    return Object.keys(properties).length > 0 ? properties : undefined;
  }, []);

  const batchItemToPreviewEntries = useCallback((item: BatchProposalItem): ProposalSubmitEntry[] => {
    switch (item.operation) {
      case 'add_relationship':
        return [{ action: 'add', entity_type: 'relationship', from_node_id: item.from_node_id, to_node_id: item.to_node_id, rel_type: item.relationship_type, justification: item.justification }];
      case 'remove_relationship':
        return [{ action: 'delete', entity_type: 'relationship', from_node_id: item.from_node_id, to_node_id: item.to_node_id, rel_type: item.relationship_type, justification: item.justification }];
      case 'delete':
        return [{ action: 'delete', entity_type: 'node', node_id: item.target_node_id, justification: item.justification }];
      case 'update':
        return (item.field_updates ?? []).map((update) => ({
          action: 'update',
          entity_type: 'node',
          node_id: item.target_node_id,
          field_name: update.field_name,
          field_value: typeof update.field_value === 'string' ? update.field_value : String(update.field_value ?? ''),
          justification: item.justification,
        }));
      case 'add':
      default:
        return [{
          action: 'add',
          entity_type: 'node',
          node_id: item.node?.id,
          node_type: item.node_type,
          node_name: item.node?.name,
          node_description: item.node?.description,
          node_properties: batchNodeToLegacyProperties(item.node),
          justification: item.justification,
        }];
    }
  }, [batchNodeToLegacyProperties]);

  const syncQueues = useCallback((items: BatchProposalItem[]): void => {
    batchQueueRef.current = items;
    queueRef.current = items.flatMap(batchItemToPreviewEntries);
    setBatchQueue([...batchQueueRef.current]);
    setQueue([...queueRef.current]);
  }, [batchItemToPreviewEntries]);

  const legacyEntryToBatchItem = (entry: ProposalSubmitEntry): BatchProposalItem => {
    if (entry.entity_type === 'relationship') {
      return {
        operation: entry.action === 'add' ? 'add_relationship' : 'remove_relationship',
        from_node_id: entry.from_node_id,
        to_node_id: entry.to_node_id,
        relationship_type: entry.rel_type,
        justification: entry.justification,
      };
    }

    if (entry.action === 'delete') {
      return {
        operation: 'delete',
        target_node_id: entry.node_id,
        justification: entry.justification,
      };
    }

    if (entry.action === 'update') {
      return {
        operation: 'update',
        target_node_id: entry.node_id,
        justification: entry.justification,
        field_updates: entry.field_name ? [{ field_name: entry.field_name, field_value: entry.field_value ?? '' }] : [],
      };
    }

    return {
      operation: 'add',
      node_type: entry.node_type,
      justification: entry.justification,
      node: {
        id: entry.node_id,
        name: entry.node_name,
        description: entry.node_description,
        properties: entry.node_properties,
        aliases: Array.isArray(entry.node_properties?.aliases)
          ? entry.node_properties.aliases.filter((alias): alias is string => typeof alias === 'string')
          : undefined,
      },
    };
  };

  const proposeBatch = useCallback(
    async (entries: BatchProposalItem[]): Promise<void> => {
      if (!sessionActive || !sessionIDRef.current || entries.length === 0) {
        if (!sessionActive || !sessionIDRef.current) {
          console.warn('[SessionContext] proposeBatch() called outside of an active session — start an edit session first.');
        }
        return;
      }

      syncQueues([...batchQueueRef.current, ...entries]);
    },
    [sessionActive, syncQueues]
  );

  const propose = useCallback(
    async (entry: ProposalSubmitEntry): Promise<void> => {
      await proposeBatch([legacyEntryToBatchItem(entry)]);
    },
    [proposeBatch]
  );

  const startSession = useCallback((parentSessionId?: string | null) => {
    if (sessionActive) return;

    const requestedSessionID = generateSessionID();
    // Set the ref immediately so cancelSession can always find the ID.
    sessionIDRef.current = requestedSessionID;
    const gen = cancelGenRef.current;

    const req: { session_id: string; depends_upon_session?: string } = { session_id: requestedSessionID };
    if (parentSessionId) {
      req.depends_upon_session = parentSessionId;
    }

    proposalApi.createSession(req)
      .then((session) => {
        // If a cancel happened while the create was in-flight, bail out.
        if (cancelGenRef.current !== gen) return;
        sessionIDRef.current = session.sessionId;
        setCurrentSessionId(session.sessionId);
        syncQueues([]);
        setSessionActive(true);
      })
      .catch((err) => {
        // If cancelled in the meantime, the error is expected.
        if (cancelGenRef.current !== gen) return;
        sessionIDRef.current = '';
        console.error('[SessionContext] failed to create session', err);
      });
  }, [sessionActive, syncQueues]);

  const resumeSession = useCallback((sessionId: string) => {
    if (sessionActive) return;
    sessionIDRef.current = sessionId;
    setCurrentSessionId(sessionId);
    syncQueues([]);
    setSessionActive(true);
  }, [sessionActive, syncQueues]);

  const saveSession = useCallback(
    async (onCommitted?: () => void): Promise<void> => {
      if (!sessionActive) return;
      const sid = sessionIDRef.current;
      const entries = [...batchQueueRef.current];

      if (sid && entries.length > 0) {
        await proposalApi.submitSessionBatch(sid, { proposals: entries });
      }

      syncQueues([]);
      sessionIDRef.current = '';
      setCurrentSessionId('');
      setSessionActive(false);

      if (!sid) {
        onCommitted?.();
        return;
      }

      if (entries.length === 0) {
        // No edits — delete the empty backend session instead of leaking it.
        try { await proposalApi.deleteSession(sid); } catch { /* best-effort */ }
        onCommitted?.();
        return;
      }
      onCommitted?.();
    },
    [sessionActive, syncQueues]
  );

  const cancelSession = useCallback(async (): Promise<void> => {
    cancelGenRef.current += 1; // invalidate any in-flight startSession
    const currentSessionID = sessionIDRef.current;

    sessionIDRef.current = '';
    setCurrentSessionId('');
    syncQueues([]);
    setSessionActive(false);

    if (!currentSessionID) return;

    try {
      await proposalApi.deleteSession(currentSessionID);
    } catch (err) {
      console.warn('[SessionContext] failed to delete session on cancel', err);
    }
  }, [syncQueues]);

  return (
    <SessionContext.Provider
      value={{ sessionActive, currentSessionId, batchQueue, queue, queueLength: batchQueue.length, propose, proposeBatch, startSession, resumeSession, saveSession, cancelSession }}
    >
      {children}
    </SessionContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/** Access the session context. Must be used inside a <SessionProvider>. */
export function useSession(): SessionContextValue {
  const ctx = useContext(SessionContext);
  if (!ctx) {
    throw new Error('useSession must be used inside a <SessionProvider>');
  }
  return ctx;
}
